import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Flex, Heading, Icon, Tab, Tabs, Text } from 'core/components';
import styled from 'styled-components';
import { Classes } from '@blueprintjs/core';
import { rgba } from 'polished';
import { sortBy, debounce } from 'lodash';
import PropTypes from 'prop-types';
import { formatArrayForDisplay } from 'app/util/formatUtils';
import TabResultsTable from './TabResultsTable';
import { defaultTabWhitelist } from './tabLists';

const CONTEXT_TYPE_VERTICAL_DETAIL_TABS = 'vertical_detail_tabs';

const TabsWrapper = styled.div`
  .${Classes.TAB_LIST} {
    border: ${({ theme }) => theme.borders.thinLighter};
    border-radius: 5px;

    > .${Classes.TAB} {
      color: ${({ theme }) => theme.colors.body};
      padding: 3px 16px;

      .${Classes.ICON} {
        color: transparent;
      }

      &:hover {
        .${Classes.ICON} {
          color ${({ theme }) => theme.colors.muted};
        }
      }

      &[aria-selected='true'] {
        font-weight: 600;

        .${Classes.ICON} {
          color: ${({ theme }) => theme.colors.body};
        }
      }
    }

    > .${Classes.TAB_INDICATOR_WRAPPER} {
      > .${Classes.TAB_INDICATOR} {
        background-color: ${({ theme }) => rgba(theme.colors.gray3, 0.15)};
      }
    }
  }
`;

function filterTabs(tabs, whitelist, blacklist, { hasGCEDevice, hasAzureDevice, hasAWSDevice }) {
  const filteredTabs = tabs.filter(
    (tab) =>
      tab &&
      tab.id &&
      whitelist.includes(tab.id) &&
      !blacklist.includes(tab.id) &&
      !(
        (!hasAzureDevice && tab.id.startsWith('az_')) ||
        (!hasGCEDevice && tab.id.startsWith('gce_')) ||
        (!hasAWSDevice && tab.id.startsWith('aws_'))
      )
  );

  return sortBy(filteredTabs, (tab) => (tab.title === 'ASNs' ? 'AAA' : tab.title.toUpperCase()));
}

@inject('$app', '$devices', '$exports', '$tabs', '$journeys')
@observer
class VerticalDetailTabs extends Component {
  static propTypes = {
    contextName: PropTypes.string,
    contextType: PropTypes.string,
    getModelSummary: PropTypes.func,
    contextMetadata: PropTypes.object,
    whitelist: PropTypes.array,
    blacklist: PropTypes.array,
    defaultTab: PropTypes.string,
    queryOverrides: PropTypes.object,
    simpleFilters: PropTypes.object,
    simpleFilterGroups: PropTypes.array
  };

  static defaultProps = {
    contextName: 'Detail Tabs',
    contextType: CONTEXT_TYPE_VERTICAL_DETAIL_TABS,
    getModelSummary: undefined,
    contextMetadata: undefined,
    whitelist: undefined,
    blacklist: undefined,
    defaultTab: undefined,
    queryOverrides: undefined,
    simpleFilters: undefined,
    simpleFilterGroups: undefined
  };

  state = {
    activeTab: undefined,
    tabs: [],
    tabResults: {} // Store results for each tab to include in context
  };

  // Debounced version of updateTabContext to avoid excessive updates
  debouncedUpdateContext = debounce(() => {
    this.updateTabContextImmediate();
  }, 500);

  constructor(props) {
    super(props);
    const { $devices, $tabs, whitelist, blacklist, defaultTab, cloud } = props;

    const allTabs = Object.values($tabs.getTabs(cloud));
    const allowableTabs = filterTabs(allTabs, whitelist || defaultTabWhitelist, blacklist || [], $devices);

    this.state.tabs = allowableTabs.map((tab) => ({ ...tab, value: tab.id, label: tab.title }));
    this.state.activeTab = defaultTab || allowableTabs[0].id;
  }

  componentDidMount() {
    const { $exports } = this.props;

    $exports.getSettings().then(({ activeTab }) => {
      if (activeTab) {
        this.setState({
          activeTab
        });
      }
    });

    // Add initial context
    this.updateTabContext();
  }

  componentDidUpdate(prevProps, prevState) {
    const { simpleFilters, simpleFilterGroups, queryOverrides, contextMetadata } = this.props;
    const { activeTab } = this.state;

    // Update context when tab changes, filters change, query changes, or metadata changes
    // Note: tabResults updates are handled separately in handleTabResultsUpdate
    const filtersChanged = simpleFilters !== prevProps.simpleFilters;
    const filterGroupsChanged = simpleFilterGroups !== prevProps.simpleFilterGroups;
    const queryChanged = queryOverrides !== prevProps.queryOverrides;
    const tabChanged = activeTab !== prevState.activeTab;
    const metadataChanged = contextMetadata !== prevProps.contextMetadata;

    if (filtersChanged || filterGroupsChanged || queryChanged || tabChanged || metadataChanged) {
      this.updateTabContext();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timeout);
    // Cancel any pending debounced context updates
    this.debouncedUpdateContext.cancel();
    this.removeTabContext();
  }

  handleTabChange = (tab) => {
    const { $exports } = this.props;

    this.setState({ activeTab: tab });
    $exports.setHash({ activeTab: tab, sortResultsBy: undefined, sortResultsDir: undefined });
  };

  /**
   * Called by TabResultsTable when query completes
   */
  handleTabResultsUpdate = (tabId, results) => {
    this.setState(
      (prevState) => {
        const existingResults = prevState.tabResults[tabId];

        // Only update if results actually changed (prevent unnecessary updates)
        // Check both loading state and results reference
        if (
          existingResults &&
          existingResults.loading === results.loading &&
          existingResults.results === results.results
        ) {
          return null; // No state change needed
        }

        return {
          tabResults: {
            ...prevState.tabResults,
            [tabId]: results
          }
        };
      },
      () => {
        // Debounce context updates to batch rapid changes
        this.debouncedUpdateContext();
      }
    );
  };

  /**
   * Updates AI context with current tab state, filters, and results
   * Use this for immediate updates (tab changes, filter changes)
   */
  updateTabContext = () => {
    // Cancel any pending debounced update
    this.debouncedUpdateContext.cancel();
    this.updateTabContextImmediate();
  };

  /**
   * Internal method that performs the actual context update
   */
  updateTabContextImmediate = () => {
    const { $journeys, contextName, contextType, simpleFilters, simpleFilterGroups, contextMetadata } = this.props;
    const { activeTab, tabs, tabResults } = this.state;

    if (!$journeys) {
      return;
    }

    const contextParts = [];
    const activeTabData = tabs.find((t) => t.id === activeTab);

    // Add header information
    contextParts.push(`Context: ${contextName}`);

    // Add metadata if provided (device info, settings, etc.)
    if (contextMetadata) {
      if (contextMetadata.state) {
        contextParts.push(`State: ${contextMetadata.state}`);
      }

      if (contextMetadata.deviceInfo) {
        contextParts.push('\nDEVICE INFORMATION:');
        Object.entries(contextMetadata.deviceInfo).forEach(([key, value]) => {
          if (value) {
            contextParts.push(`  ${key}: ${value}`);
          }
        });
      }

      if (contextMetadata.settings) {
        contextParts.push('\nANALYSIS SETTINGS:');
        Object.entries(contextMetadata.settings).forEach(([key, value]) => {
          if (value) {
            contextParts.push(`  ${key}: ${value}`);
          }
        });
      }

      if (contextMetadata.additionalInfo) {
        contextParts.push('\nADDITIONAL INFORMATION:');
        if (Array.isArray(contextMetadata.additionalInfo)) {
          contextMetadata.additionalInfo.forEach((info) => contextParts.push(`  ${info}`));
        } else {
          Object.entries(contextMetadata.additionalInfo).forEach(([key, value]) => {
            if (value) {
              contextParts.push(`  ${key}: ${value}`);
            }
          });
        }
      }
    }

    contextParts.push(`\nActive Tab: ${activeTabData?.title || activeTab || 'None'}`);

    // Add available tabs
    if (tabs.length > 0) {
      contextParts.push(`\nAvailable Tabs (${tabs.length}):`);
      tabs.forEach((tab) => {
        const isActive = tab.id === activeTab;
        contextParts.push(`  ${isActive ? '→' : ' '} ${tab.title}${isActive ? ' (current)' : ''}`);
      });
    }

    // Add filter information
    if (simpleFilters && Array.isArray(simpleFilters) && simpleFilters.length > 0) {
      const filterCount = simpleFilters.length;
      contextParts.push(`\nActive Filters (${filterCount}):`);
      simpleFilters.slice(0, 10).forEach((filter) => {
        const { filterField, operator, filterValue } = filter;
        const displayValue = Array.isArray(filterValue) ? filterValue.join(', ') : filterValue;
        const operatorStr = operator && operator !== '=' ? ` ${operator}` : '';
        contextParts.push(`  ${filterField}${operatorStr}: ${displayValue}`);
      });
      if (simpleFilters.length > 10) {
        contextParts.push(`  ...and ${simpleFilters.length - 10} more filters`);
      }
    } else if (simpleFilters && typeof simpleFilters === 'object' && Object.keys(simpleFilters).length > 0) {
      // Fallback for object-based filters (legacy support)
      const filterCount = Object.keys(simpleFilters).length;
      contextParts.push(`\nActive Filters (${filterCount}):`);
      Object.entries(simpleFilters)
        .slice(0, 10)
        .forEach(([key, value]) => {
          const displayValue = formatArrayForDisplay(value);
          contextParts.push(`  ${key}: ${displayValue}`);
        });
      if (Object.keys(simpleFilters).length > 10) {
        contextParts.push(`  ...and ${Object.keys(simpleFilters).length - 10} more filters`);
      }
    } else {
      contextParts.push('\nActive Filters: None');
    }

    // Add filter groups if present
    if (simpleFilterGroups && simpleFilterGroups.length > 0) {
      contextParts.push(`\nFilter Groups: ${simpleFilterGroups.length} groups applied`);
    }

    // Add results for active tab
    const activeTabResults = tabResults[activeTab];
    if (activeTabResults) {
      const { loading, results } = activeTabResults;

      if (loading) {
        contextParts.push(`\n${activeTabData?.title || 'Current Tab'} Results: Loading...`);
      } else if (results) {
        // Try multiple ways to detect if there's data
        let hasData = false;
        let resultSize = 0;
        let models = [];

        // Check for MobX Collection (has .size property)
        if (results.size !== undefined && results.size !== null) {
          hasData = results.size > 0;
          resultSize = results.size;
          models = results.models || [];
        }
        // Check for models array directly
        else if (results.models && Array.isArray(results.models)) {
          hasData = results.models.length > 0;
          resultSize = results.models.length;
          models = results.models;
        }
        // Check if results is directly an array
        else if (Array.isArray(results)) {
          hasData = results.length > 0;
          resultSize = results.length;
          models = results;
        }
        // Check for length property (array-like object)
        else if (results.length !== undefined && results.length !== null) {
          hasData = results.length > 0;
          resultSize = results.length;
          // Try to convert to array if it has array-like properties
          try {
            models = Array.from(results);
          } catch (e) {
            // If Array.from fails, try to access indexed properties
            models = [];
            for (let i = 0; i < results.length; i++) {
              if (results[i]) {
                models.push(results[i]);
              }
            }
          }
        }
        // Check for observable array (MobX)
        else if (results.toJS && typeof results.toJS === 'function') {
          const plainResults = results.toJS();
          if (Array.isArray(plainResults)) {
            hasData = plainResults.length > 0;
            resultSize = plainResults.length;
            models = plainResults;
          }
        }

        // Validate that models is actually an array with content
        if (!Array.isArray(models)) {
          models = [];
          hasData = false;
        }

        if (hasData && models.length > 0) {
          const sampleSize = Math.min(5, resultSize);
          contextParts.push(
            `\n${activeTabData?.title || 'Current Tab'} Results (showing ${sampleSize} of ${resultSize}):`
          );

          models.slice(0, sampleSize).forEach((model, idx) => {
            try {
              const summary = this.getModelSummary(model, activeTabData);
              contextParts.push(`  ${idx + 1}. ${summary}`);
            } catch (err) {
              // If getModelSummary fails, show basic info
              contextParts.push(`  ${idx + 1}. [Data available but format unknown]`);
            }
          });

          if (resultSize > sampleSize) {
            contextParts.push(`  ...and ${resultSize - sampleSize} more results`);
          }
        } else {
          // No data found in any recognized format
          contextParts.push(`\n${activeTabData?.title || 'Current Tab'} Results: No data available`);
        }
      } else {
        contextParts.push(`\n${activeTabData?.title || 'Current Tab'} Results: No data available`);
      }
    } else {
      contextParts.push(`\n${activeTabData?.title || 'Current Tab'} Results: Not yet loaded`);
    }

    // Add tab-specific dimensions info
    if (activeTabData?.dimension) {
      const dimensions = Array.isArray(activeTabData.dimension) ? activeTabData.dimension : [activeTabData.dimension];
      contextParts.push(`\nDimensions: ${dimensions.join(', ')}`);
    }

    // Update or add the context
    $journeys.updateChatContext({
      type: contextType,
      name: contextName,
      context: contextParts.join('\n'),
      placeholder: `Ask about ${activeTabData?.title || 'this data'}`,
      hidden: true
    });
  };

  /**
   * Get a summary of a model for the AI context
   */
  getModelSummary = (model, tabData) => {
    const { getModelSummary } = this.props;

    // Use custom summary function if provided
    if (getModelSummary) {
      try {
        return getModelSummary(model, tabData);
      } catch (err) {
        // Fall through to default handling if custom function fails
      }
    }

    // Default: build summary from dimension and key fields
    const parts = [];

    // Helper to format values safely
    const formatValue = (value) => {
      if (value === null || value === undefined) {
        return null;
      }
      if (typeof value === 'object' && 'value' in value) {
        return value.value;
      }
      if (typeof value === 'object') {
        return null;
      } // Skip objects we can't format
      return String(value);
    };

    // Helper to get value from model (handles both Backbone models and plain objects)
    const getModelValue = (key) => {
      try {
        // Try Backbone model.get()
        if (model.get && typeof model.get === 'function') {
          return model.get(key);
        }
        // Try direct property access
        if (model[key] !== undefined) {
          return model[key];
        }
        // Try attributes object
        if (model.attributes && model.attributes[key] !== undefined) {
          return model.attributes[key];
        }
      } catch (err) {
        // Ignore errors
      }
      return null;
    };

    // Try to get the main dimension value
    if (tabData?.dimension) {
      const dimensions = Array.isArray(tabData.dimension) ? tabData.dimension : [tabData.dimension];
      dimensions.forEach((dim) => {
        const value = formatValue(getModelValue(dim));
        if (value) {
          parts.push(`${dim}: ${value}`);
        }
      });
    }

    // Add key or name if available
    if (parts.length === 0) {
      const key = formatValue(getModelValue('key'));
      const name = formatValue(getModelValue('name'));
      const label = formatValue(getModelValue('label'));

      if (key) {
        parts.push(key);
      } else if (name) {
        parts.push(name);
      } else if (label) {
        parts.push(label);
      }
    }

    // If still no parts, use first few attributes
    if (parts.length === 0) {
      try {
        const attrs = model.attributes || model;
        const attrKeys = Object.keys(attrs).slice(0, 3);
        attrKeys.forEach((attrKey) => {
          // Skip internal/meta fields
          if (attrKey === 'id' || attrKey === '_id' || attrKey.startsWith('__')) {
            return;
          }
          const value = formatValue(attrs[attrKey]);
          if (value) {
            parts.push(`${attrKey}: ${value}`);
          }
        });
      } catch (err) {
        // Ignore errors
      }
    }

    // Final safety check: ensure all parts are strings
    const safeParts = parts.filter((part) => typeof part === 'string' && part.length > 0);

    return safeParts.length > 0 ? safeParts.join(', ') : 'Data available';
  };

  /**
   * Remove AI context when component unmounts
   */
  removeTabContext = () => {
    const { $journeys, contextType } = this.props;

    if (!$journeys) {
      return;
    }

    const oldContext = Array.from($journeys.activeChatContext).find((ctx) => ctx.type === contextType);
    if (oldContext) {
      $journeys.removeChatContext(oldContext);
    }
  };

  render() {
    const { queryOverrides, simpleFilters, simpleFilterGroups, $app, ...tableProps } = this.props;
    const { activeTab, tabs } = this.state;

    return (
      <TabsWrapper>
        {!$app.isExport && (
          <Heading level={2} fontSize={20}>
            Top Talkers
          </Heading>
        )}
        <Tabs onChange={this.handleTabChange} selectedTabId={activeTab} vertical renderActiveTabPanelOnly>
          {tabs.map((tab) => (
            <Tab
              key={tab.id}
              {...tab}
              title={
                <Flex alignItems="center" justifyContent="space-between" width="100%">
                  <Text>{tab.title}</Text>
                  <Icon icon="chevron-right" />
                </Flex>
              }
              visible={undefined}
              panel={
                <TabResultsTable
                  {...tableProps}
                  {...tab}
                  simpleFilters={simpleFilters}
                  simpleFilterGroups={simpleFilterGroups}
                  queryOverrides={queryOverrides}
                  onResultsUpdate={(results) => this.handleTabResultsUpdate(tab.id, results)}
                />
              }
            />
          ))}
        </Tabs>
      </TabsWrapper>
    );
  }
}

export default VerticalDetailTabs;
